// A4 (Guruhlar/kanallar/forumlar) — modul-local ma'lumot qatlami. `lib/adapters.ts`
// / `i18n/*` ni O'ZGARTIRMAYDI (ular HOT). Ba'zi backend endpointlar hali yo'q →
// fetcherlar `safeList` bilan bo'sh qaytadi (fail-soft), mutatsiyalar endpoint
// chiqqach ishlaydi (chiqmaguncha toast-xato). Ro'yxat/blok/verify — JONLI.
import { api } from '@/lib/api'
import type { FetchParams, FetchResult } from '@/components/DataTable/types'

/** Sahifalash + tez-qidiruv (server filtrsiz dormant endpointlar uchun). */
export function toResult<T>(
  rows: T[],
  params: FetchParams,
  search?: (r: T) => string,
): FetchResult<T> {
  const q = params.search.trim().toLowerCase()
  const filtered = q && search ? rows.filter((r) => search(r).toLowerCase().includes(q)) : rows
  const start = params.page * params.pageSize
  return { rows: filtered.slice(start, start + params.pageSize), total: filtered.length }
}

/** Endpoint yo'q/xato bo'lsa bo'sh ro'yxat (dashboard yiqilmasin). */
async function safeList<T>(path: string, key: string, signal: AbortSignal): Promise<T[]> {
  try {
    const data = await api.get<Record<string, T[]>>(path, { signal })
    return (data?.[key] as T[] | undefined) ?? []
  } catch {
    return []
  }
}

/* ── A4.11 — Bog'langan chatlar (kanal ↔ muhokama guruhi) — REAL:
 *  community_groups (bir jamoadagi kanal↔guruh bog'lanishi). ────────── */
export interface LinkedChatRow {
  id: number
  channel_id: number
  channel_title: string
  linked_group_id: number
  linked_group_title: string
  member_count: number
}
export function fetchLinkedChats(params: FetchParams): Promise<FetchResult<LinkedChatRow>> {
  return safeList<LinkedChatRow>('/admin/group-ops/linked', 'items', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.channel_title} ${r.linked_group_title}`),
  )
}

/* ── A4.15 — Pinned/muhim xabarlar — REAL: pinned_messages ⨝ messages ── */
export interface PinnedMessageRow {
  id: number
  group_id: number
  group_title: string
  seq: number
  sender: string
  text: string
  pinned_at: number
}
export function fetchPinnedMessages(params: FetchParams): Promise<FetchResult<PinnedMessageRow>> {
  return safeList<PinnedMessageRow>('/admin/group-ops/pinned', 'items', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.group_title} ${r.text}`),
  )
}

/* ── A4.18 — Kanal broadcast postlari — REAL: kanal xabarlari + qamrov.
 *  MyGram'da homiylik/reklama posti tushunchasi yo'q → kanal broadcast
 *  postlari (qamrov = a'zolar soni) halol ko'rsatiladi. ─────────────── */
export interface AdPostRow {
  id: number
  group_id: number
  group_title: string
  sender: string
  text: string
  reach: number
  created_at: number
}
export function fetchAdPosts(params: FetchParams): Promise<FetchResult<AdPostRow>> {
  return safeList<AdPostRow>('/admin/group-ops/ad-posts', 'items', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.group_title} ${r.sender} ${r.text}`),
  )
}

/* ── A4.20 — Monetizatsiya (boost) — REAL: channel_boosts. MyGram bepul-boost
 *  modeli → boost soni/darajasi (daromad soxta qo'yilmaydi). ────────── */
export interface MonetizationRow {
  id: number
  group_id: number
  group_title: string
  boosters: number
  level: number
  last_boost: number
}
export function fetchMonetization(params: FetchParams): Promise<FetchResult<MonetizationRow>> {
  return safeList<MonetizationRow>('/admin/group-ops/monetization', 'items', params.signal).then(
    (rows) => toResult(rows, params, (r) => `${r.group_title}`),
  )
}

/* ── Mutatsiyalar (guruh-detal amallari) ─────────────────────────── */

/** A4.23 — guruh/kanal egalariga rasmiy ogohlantirish yuborish. */
export function sendGroupWarning(id: number, text: string): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/groups/${id}/warn`, { text })
}

/** A4.24 — chatga cheklov (faqat-o'qish / yozishni to'xtatish). */
export function setGroupRestricted(
  id: number,
  restricted: boolean,
  reason: string,
): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/groups/${id}/restrict`, { restricted, reason })
}

/** A4.25 — chatdagi postlarni ommaviy o'chirish. */
export function purgeGroupPosts(id: number, reason: string): Promise<{ deleted: number }> {
  return api.post<{ deleted: number }>(`/admin/groups/${id}/purge`, { reason })
}

/** A4.26 — kanalni global qidiruvdan yashirish/qaytarish. */
export function setGroupHidden(id: number, hidden: boolean): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/groups/${id}/hide`, { hidden })
}

/** A4.31 — chatni arxivlash/arxivdan chiqarish. */
export function setGroupArchived(id: number, archived: boolean): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/groups/${id}/archive`, { archived })
}

/** A4.32 — chatni butunlay o'chirish (qaytarib bo'lmaydi). */
export function deleteGroup(id: number): Promise<{ ok: boolean }> {
  return api.del<{ ok: boolean }>(`/admin/groups/${id}`)
}

/** Modul-local matnlar (yangi qatorlar — i18n HOT, keyin keyга ko'chiriladi). */
export const GROUP_OPS_STR = {
  // A4.02
  advancedSearch: 'Kengaytirilgan qidiruv',
  advancedSearchNote: 'Tur, forum, a\'zolar soni bo\'yicha filtr + tez qidiruv.',
  // A4.03
  bulkActions: 'Ommaviy amallar',
  bulkActionsNote: 'Qatorlarni belgilang, so\'ng ommaviy blok yoki verifikatsiya qo\'llang.',
  bulkBlock: 'Ommaviy bloklash',
  bulkVerify: 'Ommaviy verifikatsiya',
  selectRowsFirst: 'Avval aniq qatorlarni belgilang',
  bulkDone: 'Bajarildi',
  bulkPartial: 'Qisman bajarildi',
  // A4.11
  linkedChats: 'Bog\'langan chatlar',
  linkedChatsNote: 'Kanalga bog\'langan muhokama guruhlari.',
  channel: 'Kanal',
  linkedGroup: 'Muhokama guruhi',
  // A4.15
  pinnedMessages: 'Pinned/muhim xabarlar',
  pinnedNote: 'Chatlarda mahkamlangan xabarlar (moderatsiya).',
  chat: 'Chat',
  message: 'Xabar',
  pinnedAt: 'Mahkamlangan',
  sender: 'Yuboruvchi',
  // A4.18 — kanal broadcast postlari (real)
  adPosts: 'Kanal postlari',
  adPostsNote: 'Kanal broadcast postlari; qamrov ≈ a\'zolar soni (real ma\'lumot).',
  reach: 'Qamrov (a\'zolar)',
  // A4.20 — boost/monetizatsiya (real)
  monetization: 'Monetizatsiya (boost)',
  monetizationNote: 'Guruh/kanal boostlari — MyGram bepul-boost modeli (channel_boosts, real).',
  boosters: 'Boost bergan',
  level: 'Daraja',
  lastBoost: 'Oxirgi boost',
  // A4.23
  sendWarning: 'Ogohlantirish yuborish',
  sendWarningNote: 'Chat egalariga rasmiy ogohlantirish yuboriladi.',
  warningPrompt: 'Ogohlantirish matni:',
  warningSent: 'Ogohlantirish yuborildi',
  // A4.24
  restrict: 'Cheklov qo\'yish',
  restrictNote: 'Chatni faqat-o\'qish rejimiga o\'tkazish.',
  restrictReason: 'Cheklov sababi (ixtiyoriy):',
  restrictConfirm: 'Chat faqat-o\'qish rejimiga o\'tkazilsinmi?',
  restricted: 'Cheklov qo\'yildi',
  // A4.25
  purgePosts: 'Ommaviy postni o\'chirish',
  purgeNote: 'Chatdagi postlarni ommaviy o\'chirish.',
  purgeReason: 'O\'chirish sababi:',
  purgeConfirm: 'Chatdagi postlar ommaviy o\'chirilsinmi? Bu qaytarib bo\'lmaydi.',
  purged: 'Postlar o\'chirildi',
  // A4.26
  hideFromSearch: 'Qidiruvdan yashirish',
  hideNote: 'Kanalni global qidiruvdan yashirish.',
  hideConfirm: 'Kanal qidiruvdan yashirilsinmi?',
  hidden: 'Yashirildi',
  // A4.31
  archive: 'Arxivlash',
  archiveNote: 'Chatni arxivga o\'tkazish.',
  archiveConfirm: 'Chat arxivlansinmi?',
  archived: 'Arxivlandi',
  // A4.32
  del: 'O\'chirish',
  delNote: 'Chatni butunlay o\'chirish.',
  delConfirm: 'Chat BUTUNLAY o\'chirilsinmi? Bu qaytarib bo\'lmaydi.',
  deleted: 'O\'chirildi',
  // umumiy
  actionDone: 'Amal bajarildi',
  actionFailed: 'Amal bajarilmadi',
  reason: 'Sabab',
  status: 'Holat',
  members: 'A\'zolar',
  created: 'Yaratilgan',
  time: 'Vaqt',
} as const
